import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Box, Button, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Celular, PlaquinhaMesa, Tablet, TelaDelivery, TelaMesaQr, TelaTablet, TelaTotem, Totem } from './mockups';

const SITE_SISCHEF = 'https://sischef.com';

const flutuar = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(-0.9em); }
`;

const semMovimento = { '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' } };

// jsdom e navegador antigo não têm IntersectionObserver: mostra direto
function useRevelar<T extends Element>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [visivel, setVisivel] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (visivel || !ref.current) return;

    const observador = new IntersectionObserver(
      ([entrada]) => {
        if (entrada?.isIntersecting) setVisivel(true);
      },
      { threshold: 0.2 },
    );
    observador.observe(ref.current);
    return () => observador.disconnect();
  }, [visivel]);

  return [ref, visivel];
}

function Revelar({ children, atraso = 0 }: { children: ReactNode; atraso?: number }): JSX.Element {
  const [ref, visivel] = useRevelar<HTMLDivElement>();

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visivel ? 1 : 0,
        transform: visivel ? 'none' : 'translateY(24px)',
        transition: `opacity 800ms ease ${atraso}ms, transform 800ms ease ${atraso}ms`,
        ...semMovimento,
      }}
    >
      {children}
    </Box>
  );
}

function Flutuando({ children, atraso = 0 }: { children: ReactNode; atraso?: number }): JSX.Element {
  return (
    <Box sx={{ animation: `${flutuar} 5s ease-in-out ${atraso}ms infinite alternate`, ...semMovimento }}>{children}</Box>
  );
}

function Palco({ children, escala }: { children: ReactNode; escala: Record<string, string> }): JSX.Element {
  return (
    <Box
      sx={(tema) => ({
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
        fontSize: escala,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '10% 5%',
          borderRadius: '50%',
          background: `radial-gradient(closest-side, ${alpha(tema.palette.primary.main, 0.18)}, transparent)`,
          filter: 'blur(8px)',
        },
      })}
    >
      <Box sx={{ position: 'relative' }}>{children}</Box>
    </Box>
  );
}

interface Canal {
  rotulo: string;
  titulo: string;
  texto: string;
  pontos: string[];
  aparelho: ReactNode;
  escala: Record<string, string>;
}

const CANAIS: Canal[] = [
  {
    rotulo: 'Delivery e retirada',
    titulo: 'Sua loja no celular do cliente',
    texto: 'Um endereço com o nome da sua loja para divulgar no Instagram e no WhatsApp. O cliente pede e escolhe se recebe em casa ou retira no balcão.',
    pontos: ['Taxa de entrega configurável', 'Horário de funcionamento da loja', 'Cupons de desconto'],
    aparelho: (
      <Flutuando>
        <Celular>
          <TelaDelivery />
        </Celular>
      </Flutuando>
    ),
    escala: { xs: '8px', md: '10px' },
  },
  {
    rotulo: 'Totem de autoatendimento',
    titulo: 'Fila menor no caixa',
    texto: 'O cliente monta o pedido sozinho, com fotos e no ritmo dele. O pedido já sai pronto para a cozinha.',
    pontos: ['Tela pensada para o toque', 'Categorias grandes e fáceis de achar', 'Senha e cupom impressos na hora'],
    aparelho: (
      <Flutuando atraso={400}>
        <Totem>
          <TelaTotem />
        </Totem>
      </Flutuando>
    ),
    escala: { xs: '7px', md: '9px' },
  },
  {
    rotulo: 'Tablet na mesa',
    titulo: 'O cardápio fica na mesa',
    texto: 'Um tablet fixo em cada mesa: o cliente pede quando quiser, chama o garçom e pede a conta com um toque.',
    pontos: ['Pedidos a qualquer hora, sem esperar o garçom', 'Chamar garçom e pedir a conta', 'Cada mesa com o seu tablet'],
    aparelho: (
      <Flutuando atraso={200}>
        <Tablet>
          <TelaTablet />
        </Tablet>
      </Flutuando>
    ),
    escala: { xs: '6.8px', sm: '9px', md: '9.5px' },
  },
  {
    rotulo: 'QR Code na mesa e na comanda',
    titulo: 'Aponte a câmera e peça',
    texto: 'Um QR Code em cada mesa ou comanda. O cliente abre o cardápio no próprio celular, sem instalar nada, e o pedido já vai com o número da mesa.',
    pontos: ['Não precisa instalar aplicativo', 'O pedido já identifica a mesa ou a comanda', 'Funciona em qualquer celular'],
    aparelho: (
      <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
        <Flutuando atraso={300}>
          <PlaquinhaMesa mesa="Mesa 7" />
        </Flutuando>
        <Box sx={{ ml: '-3em', fontSize: '0.85em', position: 'relative' }}>
          <Flutuando>
            <Celular>
              <TelaMesaQr />
            </Celular>
          </Flutuando>
        </Box>
      </Box>
    ),
    escala: { xs: '7.5px', md: '10px' },
  },
];

interface Vantagem {
  icone: ReactNode;
  titulo: string;
  texto: string;
}

const VANTAGENS: Vantagem[] = [
  { icone: <PointOfSaleOutlinedIcon />, titulo: 'Direto no Sischef', texto: 'O pedido cai no PDV e na cozinha na hora, sem digitar de novo.' },
  { icone: <MenuBookOutlinedIcon />, titulo: 'Modo vitrine', texto: 'Mostre o cardápio com fotos e preços, mesmo sem receber pedidos online.' },
  { icone: <LocalOfferOutlinedIcon />, titulo: 'Cupons e promoções', texto: 'Crie cupons de desconto para trazer o cliente de volta.' },
  { icone: <StarBorderOutlinedIcon />, titulo: 'Avaliações', texto: 'Saiba o que os clientes acharam de cada pedido.' },
];

function BotaoSite({ claro = false }: { claro?: boolean }): JSX.Element {
  return (
    <Button
      variant="contained"
      size="large"
      href={SITE_SISCHEF}
      target="_blank"
      rel="noopener"
      endIcon={<ArrowForwardIcon />}
      sx={{
        textTransform: 'none',
        px: 4,
        py: 1.25,
        fontWeight: 700,
        ...(claro && { bgcolor: '#fff', color: 'primary.main', '&:hover': { bgcolor: alpha('#fff', 0.9) } }),
      }}
    >
      Conheça o Sischef
    </Button>
  );
}

function SecaoCanal({ canal, invertida }: { canal: Canal; invertida: boolean }): JSX.Element {
  return (
    <Box component="section" sx={{ py: { xs: 6, md: 10 }, bgcolor: invertida ? 'background.paper' : 'background.default' }}>
      <Container maxWidth="lg" sx={{ px: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 8 },
            alignItems: 'center',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          }}
        >
          <Box sx={{ order: { xs: 1, md: invertida ? 2 : 1 } }}>
            <Revelar>
              <Chip label={canal.rotulo} color="primary" variant="outlined" size="small" sx={{ fontWeight: 600, mb: 2 }} />
              <Typography component="h2" variant="h4" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
                {canal.titulo}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3, fontSize: { md: '1.1rem' } }}>
                {canal.texto}
              </Typography>
              <Stack spacing={1.25}>
                {canal.pontos.map((p) => (
                  <Stack key={p} direction="row" spacing={1.25} alignItems="center">
                    <CheckCircleOutlineIcon color="primary" fontSize="small" />
                    <Typography>{p}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Revelar>
          </Box>

          <Box sx={{ order: { xs: 2, md: invertida ? 1 : 2 } }}>
            <Revelar atraso={150}>
              <Palco escala={canal.escala}>{canal.aparelho}</Palco>
            </Revelar>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export function ApresentacaoCardapioPage(): JSX.Element {
  useEffect(() => {
    document.title = 'Cardápio Sischef';
  }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', overflowX: 'hidden' }}>
      <Box
        component="header"
        sx={(tema) => ({
          pt: { xs: 6, md: 10 },
          pb: { xs: 4, md: 8 },
          background: `linear-gradient(180deg, ${alpha(tema.palette.primary.main, 0.08)}, transparent)`,
        })}
      >
        <Container maxWidth="lg" sx={{ px: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 4, md: 6 },
              alignItems: 'center',
              gridTemplateColumns: { xs: '1fr', md: '5fr 6fr' },
            }}
          >
            <Revelar>
              <Stack spacing={2.5} alignItems={{ xs: 'center', md: 'flex-start' }} textAlign={{ xs: 'center', md: 'left' }}>
                <Box component="img" src="/logo-sischef.svg" alt="Sischef" sx={{ height: 48 }} />
                <Typography component="h1" variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '2.25rem', md: '3.25rem' }, lineHeight: 1.1 }}>
                  Cardápio Sischef
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: { xs: '1.05rem', md: '1.25rem' }, maxWidth: 520 }}>
                  O cardápio digital do seu restaurante no delivery, no totem, no tablet e no QR Code da
                  mesa, com os pedidos chegando direto no seu Sischef.
                </Typography>
                <BotaoSite />
              </Stack>
            </Revelar>

            <Revelar atraso={200}>
              <Palco escala={{ xs: '6px', sm: '8px', md: '9px' }}>
                <Box sx={{ position: 'relative', pr: '8em', pb: '6em' }}>
                  <Flutuando atraso={300}>
                    <Tablet>
                      <TelaTablet />
                    </Tablet>
                  </Flutuando>
                  <Box sx={{ position: 'absolute', right: 0, bottom: 0, fontSize: '0.8em' }}>
                    <Flutuando>
                      <Celular>
                        <TelaDelivery />
                      </Celular>
                    </Flutuando>
                  </Box>
                </Box>
              </Palco>
            </Revelar>
          </Box>
        </Container>
      </Box>

      {CANAIS.map((c, i) => (
        <SecaoCanal key={c.rotulo} canal={c} invertida={i % 2 === 1} />
      ))}

      <Box component="section" sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg" sx={{ px: 2 }}>
          <Typography component="h2" variant="h4" textAlign="center" sx={{ fontWeight: 800, mb: { xs: 4, md: 6 }, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
            Feito para o dia a dia da loja
          </Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }}>
            {VANTAGENS.map((v, i) => (
              <Revelar key={v.titulo} atraso={i * 100}>
                <Paper
                  variant="outlined"
                  sx={{ p: 3, height: '100%', transition: 'border-color 300ms ease', '&:hover': { borderColor: 'primary.main' } }}
                >
                  <Box
                    sx={(tema) => ({
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: 'primary.main',
                      bgcolor: alpha(tema.palette.primary.main, 0.1),
                    })}
                  >
                    {v.icone}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {v.titulo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {v.texto}
                  </Typography>
                </Paper>
              </Revelar>
            ))}
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ px: 2, pb: { xs: 6, md: 10 } }}>
        <Container
          maxWidth="lg"
          sx={(tema) => ({
            py: { xs: 5, md: 7 },
            px: { xs: 3, md: 6 },
            borderRadius: 4,
            textAlign: 'center',
            color: '#fff',
            background: `linear-gradient(135deg, ${tema.palette.primary.main}, #ff5f93)`,
          })}
        >
          <Typography component="h2" variant="h4" sx={{ fontWeight: 800, mb: 1.5, fontSize: { xs: '1.6rem', md: '2.25rem' } }}>
            Leve o seu cardápio para todos os canais
          </Typography>
          <Typography sx={{ mb: 3, opacity: 0.9, fontSize: { md: '1.1rem' } }}>
            Um cadastro de produtos só, valendo no delivery, no totem, no tablet e na mesa.
          </Typography>
          <BotaoSite claro />
        </Container>

        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
          Quer fazer um pedido? Use o link ou o QR Code que o restaurante te enviou — cada loja tem o seu
          próprio endereço.
        </Typography>
      </Box>
    </Box>
  );
}
